import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@components/ui/Button';
import { AppContentViewer } from '@/components/apps/AppContentViewer';
import { appPublicLinkService } from '@/services/appPublicLinks';
import type { GeneratedApp } from '@/types';

function mapPublicApp(raw: Record<string, unknown>): GeneratedApp {
  const name =
    (typeof raw.app_name === 'string' && raw.app_name) ||
    (typeof raw.name === 'string' && raw.name) ||
    'Shared App';
  const description =
    (typeof raw.app_description === 'string' && raw.app_description) ||
    (typeof raw.description === 'string' && raw.description) ||
    undefined;
  return {
    ...(raw as unknown as GeneratedApp),
    id: String(raw.id ?? ''),
    name,
    description,
    app_type: (raw.app_type as GeneratedApp['app_type']) ?? 'custom',
    status: (raw.preview_status as GeneratedApp['status']) ?? 'completed',
    is_bookmarked: false,
    created_at: String(raw.created_at ?? new Date().toISOString()),
    updated_at: String(raw.updated_at ?? new Date().toISOString()),
  };
}

export function PublicAppSharePage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [app, setApp] = useState<GeneratedApp | null>(null);
  const [allowInteraction, setAllowInteraction] = useState(false);
  const [grant, setGrant] = useState<string | null>(null);

  const loadApp = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    const storedGrant = appPublicLinkService.getStoredGrant(token);
    try {
      const data = await appPublicLinkService.fetchPublicApp(token, storedGrant ?? undefined);
      if (data.password_required) {
        setPasswordRequired(true);
        setAllowInteraction(Boolean(data.allow_interaction));
        setApp(null);
        return;
      }
      if (data.app) {
        setApp(mapPublicApp(data.app));
        setAllowInteraction(Boolean(data.allow_interaction));
        setPasswordRequired(false);
        if (storedGrant) setGrant(storedGrant);
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const msg = (err.response?.data as { error?: string })?.error;
        if (status === 410) {
          setError(msg ?? 'This link has expired or been revoked.');
        } else if (status === 404) {
          setError('This share link was not found.');
        } else {
          setError(msg ?? 'Failed to load shared app.');
        }
      } else {
        setError((err as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void loadApp();
  }, [loadApp]);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setUnlocking(true);
    setError(null);
    try {
      const data = await appPublicLinkService.unlockPublicApp(token, password);
      if (data.access_grant) {
        appPublicLinkService.storeGrant(token, data.access_grant);
        setGrant(data.access_grant);
      }
      if (data.app) {
        setApp(mapPublicApp(data.app));
        setAllowInteraction(Boolean(data.allow_interaction));
        setPasswordRequired(false);
      }
    } catch (err) {
      const msg = (err as Error).message ?? 'Incorrect password';
      setError(msg);
      toast.error(msg);
    } finally {
      setUnlocking(false);
    }
  };

  const handleInteract = async () => {
    if (!token || !allowInteraction) return;
    try {
      await appPublicLinkService.recordInteraction(token, grant ?? undefined);
      toast.success('Interaction recorded');
    } catch {
      toast.error('Could not record interaction');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-khiimori-bg-base">
        <Loader2 className="w-10 h-10 animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  if (passwordRequired && !app) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-khiimori-bg-base p-6">
        <form
          onSubmit={handleUnlock}
          className="w-full max-w-md bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-xl p-6 space-y-4"
        >
          <div className="flex items-center gap-2 text-khiimori-text-primary">
            <Lock className="w-5 h-5" />
            <h1 className="text-lg font-semibold">Password required</h1>
          </div>
          <p className="text-sm text-khiimori-text-muted">
            Enter the password shared with you to view this app.
          </p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Link password"
            required
            autoFocus
            className="w-full px-3 py-2 text-sm rounded-md border border-khiimori-border bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/40"
          />
          {error && (
            <p className="text-sm text-red-500" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" disabled={unlocking} className="w-full">
            {unlocking ? 'Unlocking…' : 'View app'}
          </Button>
        </form>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-khiimori-bg-base p-6">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold text-khiimori-text-primary mb-2">Link unavailable</h1>
          <p className="text-sm text-khiimori-text-muted">{error ?? 'This shared app could not be loaded.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-khiimori-bg-base">
      <header className="border-b border-khiimori-border/10 bg-khiimori-bg-elevated/5 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-khiimori-text-muted uppercase tracking-wide mb-1">Shared on Khiimori</p>
            <h1 className="text-xl font-semibold text-khiimori-text-primary">{app.name}</h1>
          </div>
          {allowInteraction && (
            <Button variant="secondary" size="sm" onClick={() => void handleInteract()}>
              Mark interaction
            </Button>
          )}
        </div>
      </header>
      <main className="max-w-6xl mx-auto p-6 lg:p-8">
        {app.description && (
          <p className="text-sm text-khiimori-text-secondary mb-6">{app.description}</p>
        )}
        <div className="bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg overflow-hidden">
          <AppContentViewer app={app} />
        </div>
      </main>
    </div>
  );
}

export default PublicAppSharePage;
